/**
 * Created by shanie on 11/3/16.
 */
import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import { createRefetchContainer, graphql } from 'react-relay';
import { connect } from 'react-redux';

// Components
import Header from '../Headers/BasicHeader';
import FoldersRefetchWrapper from './FoldersRefetchWrapper';
import Modals from '../Modals/Modals';
import Recommendations from '../Recommendations/Recommendations';
import Toolbar from '../Toolbar/Toolbar';

// Helpers
import actions from '../../actions/FavoritesActions';
import classnames from 'classnames';
import get from 'lodash.get';
import {
    copy,
    cutoffs,
    deviceTypes,
    modals,
    mutations,
    privacySettings,
    sortBy as sortByOptions,
    tabs,
} from '../../constants';
import trackPageView from '../../helpers/trackPageView';
import trackFavoritesEvent from '../../helpers/trackFavoritesEvent';
import authModalLoader from 'dibs-buyer-layout/src/utils/AuthModalLoader';

// Mutations
import ModifyPortfolios from '../../mutations/ModifyPortfolios';

// Styles
import styles from '../Items/styles/ItemsContainer.scss';

const refetchOptions = { force: true };

class FoldersContainer extends React.Component {
    constructor(props) {
        super(props);

        this.ticking = false;

        this.createFolder = this.createFolder.bind(this);
        this.folderMutations = this.folderMutations.bind(this);
        this.handleMutationResponse = this.handleMutationResponse.bind(this);
    }

    componentWillMount() {
        if (this.props.createFolderOnLoad) {
            this.createFolder();
        }
        if (this.props.tabChanged) {
            this.props.resetTotalResults();
            this.props.setFullPageLoading(true);
            // Need to forceFetch after every transition from null-state page
            // because if folders tab container is rendering, it means that the user
            // engaged with the null-state page and added a folder to their folders
            this.props.relay.refetch(
                fragmentVariables => fragmentVariables,
                null,
                () => {
                    if (this.props.openModalType === modals.REMOVE_FOLDERS) {
                        this.handleMutationResponse(
                            mutations.types.REMOVE,
                            mutations.responses.SUCCESS
                        );
                    } else {
                        this.props.setMutationResponse('');
                    }
                    this.props.setFullPageLoading(false);
                },
                refetchOptions
            );
        }
    }

    componentDidMount() {
        window.scrollTo(0, 0);
        trackPageView();
    }

    componentWillReceiveProps(nextProps) {
        const createFolderOnLoad = !!nextProps.createFolderOnLoad && !this.props.createFolderOnLoad;
        // check if specific props have changed and refetch items
        const isSortByChanged = nextProps.foldersSort !== this.props.foldersSort;

        if (createFolderOnLoad) {
            this.createFolder();
        }

        // reset modal type after modal has been shown once
        if (
            nextProps.openModalType === modals.REMOVE_FOLDERS &&
            nextProps.mutationResponse === mutations.responses.COMPLETE
        ) {
            this.props.setOpenModalType('');
        }

        if (isSortByChanged) {
            this.props.setFullPageLoading(true);
            this.props.resetTotalResults();

            const refetchVariables = fragmentVariables => ({
                ...fragmentVariables,
                foldersSort: nextProps.foldersSort,
            });

            this.props.relay.refetch(
                refetchVariables,
                null,
                () => {
                    this.props.setFullPageLoading(false);
                },
                refetchOptions
            );
        }
    }

    componentDidUpdate(prevProps) {
        const { routerNavigation, isLoadingFullPage } = this.props;

        // hide page overlay loader when navigating through router
        if (routerNavigation && !prevProps.routerNavigation && isLoadingFullPage) {
            this.props.setFullPageLoading(false);
        }
    }

    createFolder() {
        this.props.setMutationResponse('');
        this.props.setOpenModalType(modals.CREATE_FOLDER);
        this.props.setModalOpen(true);
        this.props.resetTotalResults();
    }

    handleMutationResponse(action, response) {
        // Need to forceFetch after every mutation so that post-mutation
        // back-end changes are visually reflected in UI
        this.props.relay.refetch(
            fragmentVariables => fragmentVariables,
            null,
            () => {
                if (action === mutations.types.REORDER) {
                    this.props.setReordered(false);
                } else {
                    if (action === mutations.types.SHARE) {
                        this.props.resetEmailState();
                    } else if (authModalLoader.isPaused()) {
                        authModalLoader.resume().then(() => {
                            this.props.setShowErrorSuccessModal(true);
                            this.props.setModalOpen(true);
                            this.props.setMutationResponse(response);
                        });
                    } else {
                        this.props.setShowErrorSuccessModal(true);
                        this.props.setModalOpen(true);
                        this.props.setMutationResponse(response);
                    }
                    this.props.setCreateFolderOnLoad(false);
                    this.props.selectItems([]);
                    this.props.setNewPortfolioName('');
                    this.props.setPortfolioDescription('');
                    this.props.setPortfolioPrivacy(privacySettings.PUBLIC);
                    this.props.resetUiState();
                }
                this.props.setFullPageLoading(false);
            },
            refetchOptions
        );
    }

    folderMutations({
        action,
        description,
        name,
        privacyLevel,
        targetPortfolioId,
        startPortfolioId,
        endPortfolioId,
    }) {
        const { relay, selectedItems, viewer } = this.props;

        this.props.setFullPageLoading(true);
        this.props.setMutationResponse('');
        this.props.setModalOpen(false);

        if (action === mutations.types.REORDER) {
            this.props.setReordered(true);
        }

        ModifyPortfolios.commit(relay.environment, {
            action,
            description,
            endPortfolioId,
            name,
            privacyLevel,
            portfolioIds: selectedItems.map(portfolio => portfolio.portfolioId),
            portfoliosType: 'FOLDER',
            startPortfolioId,
            targetPortfolioId,
            userId: get(viewer, 'user.serviceId'),
        })
            .then(() => {
                trackFavoritesEvent({
                    action,
                    type: tabs.FOLDER,
                    setShouldTrackReordering: this.props.setShouldTrackReordering,
                    shouldTrackReordering: this.props.shouldTrackReordering,
                });
                this.handleMutationResponse(action, mutations.responses.SUCCESS);
            })
            .catch(() => {
                this.handleMutationResponse(action, mutations.responses.ERROR);
            });
    }

    render() {
        const {
            relay,
            deviceType,
            first,
            launchModal,
            selectedItemIds,
            selectedItems,
            selecting,
            stickyToolbar,
            reordered,
            reordering,
            totalResults,
            foldersSort,
            viewer,
        } = this.props;

        const stickyClasses = classnames(styles.container, {
            [styles.sticky]: stickyToolbar && deviceType !== deviceTypes.MOBILE,
            [styles.slideIn]: selecting,
        });

        const selectOptions = [
            { label: copy.sortBy.CUSTOM, value: sortByOptions.CUSTOM },
            { label: copy.sortBy.ALPHABETICAL, value: sortByOptions.ALPHABETICAL },
            { label: copy.sortBy.MODIFIED, value: sortByOptions.MODIFIED },
        ];

        const userId = get(viewer, 'user.serviceId');

        if (totalResults === 0 || !!this.props.createFolderOnLoad) {
            return (
                <Fragment>
                    <Recommendations
                        hasUserId={!!userId}
                        trending={viewer}
                        userId={userId}
                        viewer={viewer}
                    />
                    <Modals
                        folderMutations={({ ...args }) => this.folderMutations({ ...args })}
                        modalOpen={this.props.modalOpen}
                        mutationResponse={this.props.mutationResponse}
                        openModalType={this.props.openModalType}
                        selectedItems={this.props.selectedItems}
                        showErrorSuccessModal={this.props.showErrorSuccessModal}
                    />
                </Fragment>
            );
        }

        return (
            <Fragment>
                <Header
                    onClick={() => launchModal(modals.CREATE_FOLDER)}
                    title="All Folders"
                    totalItems={totalResults}
                />
                <div className={stickyClasses}>
                    <Toolbar
                        relay={relay}
                        onCreateFolder={() => launchModal(modals.CREATE_FOLDER)}
                        selectedItemIds={selectedItemIds}
                        selectedItems={selectedItems}
                        selectOptions={selectOptions}
                        showSearchBar={false}
                        title="All Folders"
                        totalItems={totalResults}
                        userId={userId}
                        viewer={viewer}
                    />
                </div>
                {stickyToolbar && deviceType !== deviceTypes.MOBILE ? (
                    <div className={styles.shadow} />
                ) : null}
                {totalResults > cutoffs.MAX_ITEMS ? (
                    <div className={styles.cutoff}>{copy.cuttoffs.FOLDERS}</div>
                ) : null}
                <FoldersRefetchWrapper
                    first={first}
                    onDrop={(...args) => this.folderMutations(...args)}
                    reordered={reordered}
                    reordering={reordering}
                    selectedItems={selectedItems}
                    sortBy={foldersSort}
                    viewer={viewer}
                />
                <Modals
                    folderMutations={({ ...args }) => this.folderMutations({ ...args })}
                    modalOpen={this.props.modalOpen}
                    mutationResponse={this.props.mutationResponse}
                    openModalType={this.props.openModalType}
                    selectedItems={this.props.selectedItems}
                    showErrorSuccessModal={this.props.showErrorSuccessModal}
                />
            </Fragment>
        );
    }
}

FoldersContainer.propTypes = {
    deviceType: PropTypes.string,
    createFolderOnLoad: PropTypes.bool,
    first: PropTypes.number,
    foldersSort: PropTypes.string,
    hasUserId: PropTypes.bool,
    launchModal: PropTypes.func,
    modalOpen: PropTypes.bool,
    mutationResponse: PropTypes.string,
    numItems: PropTypes.number,
    openModalType: PropTypes.string,
    isLoadingFullPage: PropTypes.bool,
    isLoadingPartialPage: PropTypes.bool,
    relay: PropTypes.object,
    reordered: PropTypes.bool,
    reordering: PropTypes.bool,
    resetEmailState: PropTypes.func,
    resetUiState: PropTypes.func,
    resetTotalResults: PropTypes.func,
    routerNavigation: PropTypes.bool,
    selectItems: PropTypes.func,
    selectedItemIds: PropTypes.array,
    selectedItems: PropTypes.array,
    selecting: PropTypes.bool,
    setCreateFolderOnLoad: PropTypes.func,
    setFullPageLoading: PropTypes.func,
    setNumItems: PropTypes.func,
    setModalOpen: PropTypes.func,
    setMutationResponse: PropTypes.func,
    setPortfolioDescription: PropTypes.func,
    setNewPortfolioName: PropTypes.func,
    setPortfolioPrivacy: PropTypes.func,
    setOpenModalType: PropTypes.func,
    setReordered: PropTypes.func,
    setShowErrorSuccessModal: PropTypes.func,
    showErrorSuccessModal: PropTypes.bool,
    setShouldTrackReordering: PropTypes.func,
    stickyToolbar: PropTypes.bool,
    tabChanged: PropTypes.bool,
    totalResults: PropTypes.number,
    shouldTrackReordering: PropTypes.bool,
    sortBy: PropTypes.string,
    userId: PropTypes.string,
    viewer: PropTypes.object.isRequired,
};

const mapStateToProps = state => {
    return {
        deviceType: state.favorites.uiState.deviceType,
        foldersSort: state.favorites.uiState.foldersSort,
        createFolderOnLoad: state.favorites.uiState.createFolderOnLoad,
        modalOpen: state.favorites.modalState.modalOpen,
        mutationResponse: state.favorites.uiState.mutationResponse,
        openModalType: state.favorites.modalState.openModalType,
        reordering: state.favorites.uiState.reordering,
        selecting: state.favorites.uiState.selecting,
        stickyToolbar: state.favorites.uiState.stickToolbar,
        tabChanged: state.favorites.uiState.tabChanged,
        shouldTrackReordering: state.favorites.uiState.shouldTrackReordering,
        showErrorSuccessModal: state.favorites.modalState.showErrorSuccessModal,
        totalResults: state.favorites.uiState.totalResults,
    };
};

const mapDispatchToProps = dispatch => {
    return {
        resetUiState: () => {
            dispatch(actions.resetUiState());
        },
        resetTotalResults: () => {
            dispatch(actions.setTotalResults({ totalResults: -1 }));
        },
        selectItems: selectedItems => {
            dispatch(actions.selectItems({ selectedItems }));
        },
        setCreateFolderOnLoad: createFolderOnLoad => {
            dispatch(actions.setCreateFolderOnLoad({ createFolderOnLoad }));
        },
        setFullPageLoading: isLoadingFullPage => {
            dispatch(actions.setFullPageLoading({ isLoadingFullPage }));
        },
        setModalOpen: modalOpen => {
            dispatch(actions.setModalOpen({ modalOpen }));
        },
        setMutationResponse: mutationResponse => {
            dispatch(actions.setMutationResponse({ mutationResponse }));
        },
        setPortfolioDescription: newPortfolioDescription => {
            dispatch(actions.setPortfolioDescription({ newPortfolioDescription }));
        },
        setNewPortfolioName: newPortfolioName => {
            dispatch(actions.setNewPortfolioName({ newPortfolioName }));
        },
        setPortfolioPrivacy: newPortfolioPrivacy => {
            dispatch(actions.setPortfolioPrivacy({ newPortfolioPrivacy }));
        },
        setNumItems: first => {
            dispatch(actions.setNumItems({ first }));
        },
        setOpenModalType: openModalType => {
            dispatch(actions.setOpenModalType({ openModalType }));
        },
        setReordered: reordered => {
            dispatch(actions.setReordered({ reordered }));
        },
        setShouldTrackReordering: shouldTrackReordering => {
            dispatch(actions.setShouldTrackReordering({ shouldTrackReordering }));
        },
        setShowErrorSuccessModal: showErrorSuccessModal => {
            dispatch(actions.setShowErrorSuccessModal({ showErrorSuccessModal }));
        },
    };
};

const ReduxWrapper = connect(
    mapStateToProps,
    mapDispatchToProps
)(FoldersContainer);
FoldersContainer = createRefetchContainer(
    ReduxWrapper,
    {
        viewer: graphql`
            fragment FoldersContainer_viewer on Viewer {
                ...Recommendations_trending
                user(userId: $userId) @include(if: $hasUserId) {
                    serviceId
                    isVerifiedTrade
                    profile {
                        currency
                    }
                }
                ...FoldersRefetchWrapper_viewer
            }
        `,
    },
    graphql`
        query FoldersContainerRefetchQuery(
            $first: Int!
            $after: String
            $hasUserId: Boolean!
            $portfolioTypes: [PortfolioTypes]
            $foldersSort: PortfoliosSortByTypes
            $loadPortfolioData: Boolean!
            $selectedItemIds: [String]!
            $userId: String!
            $userIds: [String]!
        ) {
            viewer {
                ...FoldersContainer_viewer
            }
        }
    `
);

export default FoldersContainer;
